import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/** How long the success tick stays on the button, in milliseconds. */
const ADDED_DURATION = 2000;

/**
 * Failsafe for the in-flight state, in milliseconds. The button normally clears
 * when the cart confirms or reports an error; this only matters if neither ever
 * arrives, so the shopper is never left with a button they cannot press.
 */
const BUSY_FAILSAFE = 8000;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} [productImage] - Product image, absent when the product has none
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * The bar stands in for the buy box whenever the buy box is off screen — above
 * it or below it — so the quantity control and add-to-cart action are always
 * within reach without the two ever being shown at once. It steps aside for the
 * footer, whose links it would otherwise cover.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number | undefined} */
  #busyFailsafe;

  /** @type {MutationObserver | null} */
  #targetBusyObserver = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {Element | null} */
  #buyBoxElement = null;

  /** @type {Element | null} */
  #footerElement = null;

  /** @type {boolean} */
  #buyBoxInView = false;

  /** @type {boolean} */
  #footerInView = false;

  /**
   * Outcome of an add that the cart has already reported, held back while the
   * buy box button is still in flight so the two settle together.
   * @type {boolean | null}
   */
  #pendingOutcome = null;

  /**
   * The most recent purchase selection, so the bar can restore it after a
   * variant change rebuilds its markup from the one-time server state.
   * @type {{ mode: string, planId: string, ctaLabel: string } | null}
   */
  #lastSellingPlan = null;

  connectedCallback() {
    super.connectedCallback();

    this.#cacheTargetButton();
    this.#watchVisibility();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    target?.addEventListener(ThemeEvents.sellingPlanUpdate, this.#handleSellingPlanUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#targetBusyObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Caches the product form's own add-to-cart button, which the bar's button
   * clicks so the cart always receives the buy box's variant, quantity and plan.
   */
  #cacheTargetButton() {
    const productForm = this.#getProductForm();
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
    this.#watchTargetButton();
  }

  /**
   * One observer watches both things the bar's visibility depends on: the buy
   * box, so the shopper never sees two sets of the same controls, and the footer,
   * whose links the bar would otherwise cover. Targets are told apart by
   * identity, so a single callback serves both.
   */
  #watchVisibility() {
    this.#visibilityObserver ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.#buyBoxElement) this.#buyBoxInView = entry.isIntersecting;
        else if (entry.target === this.#footerElement) this.#footerInView = entry.isIntersecting;
      }
      this.#applyVisibility();
    });

    // Themes migrated from 2.0 have no footer element, only the section group.
    // The footer outlives a variant change, so it is only ever observed once.
    if (!this.#footerElement) {
      this.#footerElement = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
      if (this.#footerElement) this.#visibilityObserver.observe(this.#footerElement);
    }

    this.#observeBuyBox();
  }

  /**
   * Points the observer at the current buy box. A variant change re-renders that
   * markup, so the old node is dropped and the new one picked up, leaving the
   * footer's subscription untouched. A page with no buy box leaves the bar
   * visible rather than hiding it.
   */
  #observeBuyBox() {
    const next = this.#getProductForm()?.closest('.buy-buttons-block') ?? null;
    if (next === this.#buyBoxElement) return;

    if (this.#buyBoxElement) this.#visibilityObserver?.unobserve(this.#buyBoxElement);
    this.#buyBoxElement = next;

    if (next) {
      this.#visibilityObserver?.observe(next);
    } else {
      this.#buyBoxInView = false;
      this.#applyVisibility();
    }
  }

  /**
   * The bar stands in for the buy box, so it shows only where neither the buy box
   * nor the footer is on screen. Both observers report in here rather than
   * setting the attribute themselves, so neither can undo the other's decision.
   */
  #applyVisibility() {
    const visible = !this.#buyBoxInView && !this.#footerInView;
    this.refs.stickyBar.dataset.stuck = visible ? 'true' : 'false';
  }

  /**
   * Follows the buy box button's in-flight state. Both buttons are on screen at
   * the same time, so an add started from the buy box has to read the same way
   * down here, and neither may drop out of its pending state before the other.
   */
  #watchTargetButton() {
    this.#targetBusyObserver?.disconnect();

    const target = this.#targetAddToCartButton;
    if (!target) return;

    this.#targetBusyObserver = new MutationObserver(() => {
      if (target.getAttribute('aria-busy') === 'true') {
        this.#markBusy();
        return;
      }

      // The real button has finished. Apply whatever the cart reported while it
      // was still working.
      if (this.#pendingOutcome !== null) {
        const outcome = this.#pendingOutcome;
        this.#pendingOutcome = null;
        this.#settleButton(outcome, true);
      }
    });

    this.#targetBusyObserver.observe(target, { attributes: true, attributeFilter: ['aria-busy'] });
  }

  /**
   * Puts the bar's button into its pending state.
   */
  #markBusy() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton || addToCartButton.getAttribute('aria-busy') === 'true') return;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (this.#busyFailsafe) clearTimeout(this.#busyFailsafe);
    this.#pendingOutcome = null;

    addToCartButton.removeAttribute('data-added');
    addToCartButton.setAttribute('aria-busy', 'true');
    addToCartButton.disabled = true;
    this.#busyFailsafe = setTimeout(() => this.#settleButton(false, true), BUSY_FAILSAFE);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;

    // Ignore a second press while the first is still in flight.
    if (this.refs.addToCartButton?.getAttribute('aria-busy') === 'true') return;

    // No variant picked yet. Pressing the buy box button submits its form, which
    // scrolls to the first unpicked option and shows its message. Nothing gets
    // added, so the bar skips its pending state and the fly-to-cart.
    if (this.refs.addToCartButton?.getAttribute('aria-disabled') === 'true') {
      this.#targetAddToCartButton.click();
      return;
    }

    // Show the request, not the click. The success tick is applied only once the
    // cart confirms, so a failed add can no longer read as a successful one.
    this.#markBusy();

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    this.#playFlyToCart();
  };

  /**
   * Flies a copy of the product image to the cart icon. Decoration only — it is
   * skipped when there is no image or no cart icon to fly to, and it never
   * decides what the button shows.
   */
  #playFlyToCart() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.refs.productImage;
    // offsetParent is null when the image is hidden, which it is on mobile.
    if (!cartIcon || !image || image.offsetParent === null) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${image.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = image;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Returns the button to a usable state once the cart has responded.
   * @param {boolean} succeeded
   * @param {boolean} [force] - Settle even if the buy box button is still busy.
   */
  #settleButton(succeeded, force = false) {
    const { addToCartButton } = this.refs;
    if (!addToCartButton || addToCartButton.getAttribute('aria-busy') !== 'true') return;

    // The cart reports back before product-form.js releases the buy box button.
    // Clearing now would leave one button idle beside one still spinning, so the
    // outcome waits for the observer above.
    if (!force && this.#targetAddToCartButton?.getAttribute('aria-busy') === 'true') {
      this.#pendingOutcome = succeeded;
      return;
    }

    this.#pendingOutcome = null;
    if (this.#busyFailsafe) clearTimeout(this.#busyFailsafe);
    addToCartButton.removeAttribute('aria-busy');
    addToCartButton.disabled = false;

    if (!succeeded) return;

    addToCartButton.dataset.added = 'true';
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton?.removeAttribute('data-added');
    }, ADDED_DURATION);
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    this.#cacheTargetButton();
    this.#observeBuyBox();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // The morph brought in freshly rendered markup, which always represents the
    // one-time state at a quantity of one, so both of the shopper's choices have
    // been painted over and have to be put back.
    this.#applySellingPlan();
    this.#applyPageQuantity();
  };

  /**
   * Brings the bar's quantity back in line with the buy box.
   *
   * The server renders the bar at a quantity of one because it has no way of
   * knowing what the shopper picked, so a variant change would otherwise leave
   * the bar showing one beside a buy box showing three. The buy box remains the
   * source of truth, as it is for the live sync.
   *
   * The copied value is then put through the bar's own constraints. Both this and
   * product-form.js act on the same variant event, so the buy box may not have
   * snapped its value to the new variant's rules yet; re-applying them here means
   * the bar cannot show a quantity the new variant does not allow, whichever of
   * the two runs first.
   */
  #applyPageQuantity() {
    const pageSelector = this.#getPageQuantitySelector();
    if (!pageSelector || typeof pageSelector.getValue !== 'function') return;

    const quantity = Number(pageSelector.getValue());
    if (!Number.isFinite(quantity)) return;

    const barSelector = this.#getBarQuantitySelector();
    this.#mirrorQuantity(barSelector, quantity);

    // The morph brought in markup rendered for the new variant, so the bar's own
    // input already carries that variant's min, max and step.
    const input = barSelector?.querySelector('input[ref="quantityInput"]');
    if (input instanceof HTMLInputElement) {
      barSelector.updateConstraints?.(input.min, input.max || null, input.step);
    }
  }

  /**
   * Handles a change of purchase option, plan or delivery frequency.
   * @param {CustomEvent} event
   */
  #handleSellingPlanUpdate = (event) => {
    this.#lastSellingPlan = event.detail;
    this.#applySellingPlan();
  };

  /**
   * Brings the bar's call to action in line with the buy box, so a shopper who
   * picked a subscription sees Subscribe rather than Add to cart.
   *
   * The bar shows no price. What a shopper sees depends on their customer type —
   * distributors get UC, SRP and BV rather than one figure — so a price written
   * here would contradict the buy box for them.
   */
  #applySellingPlan() {
    const detail = this.#lastSellingPlan;
    if (!detail?.ctaLabel) return;

    const label = this.querySelector('.add-to-cart-text__label');
    if (label instanceof HTMLElement) label.textContent = detail.ctaLabel;
  }

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Settles the button once the cart has responded, and clears the puppet flag.
   * @param {Event} event - The cart update or cart error event.
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    // Settles whichever button started the add: the bar follows the buy box into
    // the pending state, so both report the same result. If neither is pending
    // this is a no-op.
    this.#settleButton(event.type !== ThemeEvents.cartError);
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    const quantity = Number(event.detail.quantity);
    if (!Number.isFinite(quantity)) return;

    // The page's selector is the one that counts — its input is inside the form
    // that actually gets submitted. The bar's copy only mirrors it.
    const target =
      event.target instanceof Node && this.contains(event.target)
        ? this.#getPageQuantitySelector()
        : this.#getBarQuantitySelector();

    this.#mirrorQuantity(target, quantity);
  };

  /**
   * Copies a quantity into the other selector, skipping the write when it already
   * agrees. That equality check is what stops the two from bouncing updates off
   * each other, since this event is global and both of them raise it.
   *
   * @param {any} selector
   * @param {number} quantity
   */
  #mirrorQuantity(selector, quantity) {
    if (!selector || typeof selector.getValue !== 'function') return;
    if (Number(selector.getValue()) === quantity) return;

    selector.setValue(String(quantity));
    // Keeps the plus/minus buttons disabled at the product's own limits.
    selector.updateButtonStates?.();
  }

  /** @returns {any} The selector inside the bar. */
  #getBarQuantitySelector() {
    return this.querySelector('quantity-selector-component');
  }

  /** @returns {any} The selector in the buy box. */
  #getPageQuantitySelector() {
    return this.#getProductForm()?.querySelector('quantity-selector-component') ?? null;
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
